import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Inbox,
  Search,
  Filter,
  RefreshCw,
  Shield,
  Clock,
  Eye,
  Mail,
  User as UserIcon,
  Building2,
  Layers,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { serviceRequestsAPI } from '../services/api';

const STATUS_OPTIONS = ['New', 'In Progress', 'Reviewed', 'Closed'];

const STATUS_STYLES = {
  New: 'bg-rose-100 text-rose-700 border-rose-200',
  'In Progress': 'bg-amber-100 text-amber-700 border-amber-200',
  Reviewed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Closed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const CATEGORY_LABELS = {
  content: 'Content',
  delivery: 'Delivery',
  program: 'Program',
  event: 'Event',
  platform: 'Platform',
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShortDate = (value) => {
  if (!value) return '-';
  const parts = String(value).split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts.map(Number);
    if (y && m && d) {
      return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const ServiceRequestsPage = () => {
  const { isAdmin, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('New');
  const [reviewComments, setReviewComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { ids: [...] }
  const [deleting, setDeleting] = useState(false);

  const categoryBreakdown = useMemo(() => {
    const counts = {};
    requests.forEach((r) => {
      const label = r.category_name || CATEGORY_LABELS[r.category_id] || r.category_id || 'Uncategorized';
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }, [requests]);

  const urgencyBreakdown = useMemo(() => {
    const counts = {};
    requests.forEach((r) => {
      const label = r.urgency || 'Unspecified';
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }, [requests]);

  const displayedRequests = useMemo(() => {
    return requests.filter((r) => {
      if (categoryFilter) {
        const label = r.category_name || CATEGORY_LABELS[r.category_id] || r.category_id || 'Uncategorized';
        if (label !== categoryFilter) return false;
      }
      if (urgencyFilter && (r.urgency || 'Unspecified') !== urgencyFilter) return false;
      return true;
    });
  }, [requests, categoryFilter, urgencyFilter]);

  const toggleCategoryFilter = (label) => setCategoryFilter((prev) => (prev === label ? '' : label));
  const toggleUrgencyFilter = (label) => setUrgencyFilter((prev) => (prev === label ? '' : label));

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const data = await serviceRequestsAPI.getAll(params);
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch service requests:', error);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const data = await serviceRequestsAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch service request stats:', error);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRequests]);

  const openDetail = (req) => {
    setSelected(req);
    setReviewStatus(req.status || 'New');
    setReviewComments(req.review_comments || '');
  };

  const handleSaveReview = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      await serviceRequestsAPI.review(selected.id, {
        status: reviewStatus,
        review_comments: reviewComments,
      });
      toast.success('Service request updated');
      setSelected(null);
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error('Failed to update service request:', error);
      toast.error(error.response?.data?.detail || 'Failed to update service request');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? displayedRequests.map((r) => r.id) : []);
  };

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await Promise.all(deleteTarget.ids.map((id) => serviceRequestsAPI.delete(id)));
      toast.success(`${deleteTarget.ids.length} service request${deleteTarget.ids.length > 1 ? 's' : ''} deleted`);
      setSelectedIds((prev) => prev.filter((id) => !deleteTarget.ids.includes(id)));
      setDeleteTarget(null);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete service request(s)');
    } finally {
      setDeleting(false);
    }
  };

  if (false) {
    return (
      <div className="text-center py-12" data-testid="service-requests-page">
        <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">Only administrators can view service requests.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="service-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Inbox className="w-6 h-6" />
            Service Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Inbound requests submitted from the Technofocus website, awaiting team triage
          </p>
        </div>
        <Button onClick={() => { fetchRequests(); fetchStats(); }} variant="outline" className="gap-2" data-testid="refresh-service-requests-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {isAdmin() && selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5" data-testid="bulk-selection-bar">
          <p className="text-sm text-rose-800 font-medium">{selectedIds.length} selected</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} data-testid="clear-selection-btn">
              Clear
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="gap-1.5"
              onClick={() => setDeleteTarget({ ids: [...selectedIds] })}
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${!filters.status && !categoryFilter && !urgencyFilter ? 'ring-2 ring-blue-400' : ''}`}
            onClick={() => { setFilters((prev) => ({ ...prev, status: '' })); setCategoryFilter(''); setUrgencyFilter(''); }}
            data-testid="tile-total-requests"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Inbox className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="stat-total-requests">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${filters.status === 'New' ? 'ring-2 ring-rose-400' : ''}`}
            onClick={() => setFilters((prev) => ({ ...prev, status: prev.status === 'New' ? '' : 'New' }))}
            data-testid="tile-needs-triage"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-rose-100 rounded-lg">
                  <Clock className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="stat-new-requests">{stats.new_count}</p>
                  <p className="text-sm text-muted-foreground">Needs Triage</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${filters.status === 'In Progress' ? 'ring-2 ring-amber-400' : ''}`}
            onClick={() => setFilters((prev) => ({ ...prev, status: prev.status === 'In Progress' ? '' : 'In Progress' }))}
            data-testid="tile-in-progress"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Layers className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="stat-in-progress-requests">{stats.in_progress_count}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category & Urgency Breakdown Tiles */}
      {(categoryBreakdown.length > 0 || urgencyBreakdown.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categoryBreakdown.length > 0 && (
            <Card data-testid="category-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground font-medium">Requests by Category</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {categoryBreakdown.map(({ label, count }) => (
                  <button
                    key={label}
                    onClick={() => toggleCategoryFilter(label)}
                    data-testid={`category-tile-${label.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-all hover:shadow-sm ${
                      categoryFilter === label
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-muted/40 border-border hover:bg-muted'
                    }`}
                  >
                    <span className="font-semibold">{count}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
          {urgencyBreakdown.length > 0 && (
            <Card data-testid="urgency-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground font-medium">Requests by Urgency</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {urgencyBreakdown.map(({ label, count }) => (
                  <button
                    key={label}
                    onClick={() => toggleUrgencyFilter(label)}
                    data-testid={`urgency-tile-${label.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-all hover:shadow-sm ${
                      urgencyFilter === label
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-muted/40 border-border hover:bg-muted'
                    }`}
                  >
                    <span className="font-semibold">{count}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}


      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value === 'all' ? '' : value }))}
            >
              <SelectTrigger data-testid="status-filter-select">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-requests-input"
                placeholder="Search name, email, category..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => { setFilters({ status: '', search: '' }); setCategoryFilter(''); setUrgencyFilter(''); }}
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : displayedRequests.length > 0 ? (
            <Table data-testid="service-requests-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {isAdmin() && (
                      <Checkbox
                        checked={displayedRequests.length > 0 && selectedIds.length === displayedRequests.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    )}
                  </TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Request ID</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Target Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedRequests.map((req) => (
                  <TableRow key={req.id} data-testid={`service-request-row-${req.id}`}>
                    <TableCell>
                      {isAdmin() && (
                        <Checkbox
                          checked={selectedIds.includes(req.id)}
                          onCheckedChange={(checked) => toggleSelectOne(req.id, checked)}
                          data-testid={`select-request-${req.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(req.submitted_at)}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground" data-testid={`request-external-id-${req.id}`}>
                      {req.external_id || '-'}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{req.req_name}</p>
                      <p className="text-xs text-muted-foreground">{req.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{req.category_name || CATEGORY_LABELS[req.category_id] || req.category_id}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{req.urgency || '-'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formatShortDate(req.target_date)}</TableCell>
                    <TableCell>
                      <Badge className={`border ${STATUS_STYLES[req.status] || STATUS_STYLES.New}`}>
                        {req.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetail(req)}
                          title="View / Review"
                          data-testid={`view-service-request-${req.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            onClick={() => setDeleteTarget({ ids: [req.id] })}
                            data-testid={`delete-service-request-${req.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No service requests found</h3>
              <p className="text-muted-foreground">
                {filters.status || filters.search || categoryFilter || urgencyFilter
                  ? 'Try adjusting your filters'
                  : 'New requests submitted from the Technofocus website will appear here'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail / Review Dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="service-request-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Inbox className="w-5 h-5" />
                  Service Request Details
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{selected.req_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selected.email}</span>
                  </div>
                  <div data-testid="detail-dialog-external-id"><span className="text-muted-foreground">Request ID: </span><span className="font-mono">{selected.external_id || '-'}</span></div>
                  <div><span className="text-muted-foreground">Role: </span>{selected.req_role || '-'}</div>
                  <div><span className="text-muted-foreground">Sponsor: </span>{selected.sponsor || '-'}</div>
                  <div><span className="text-muted-foreground">Target Date: </span>{formatShortDate(selected.target_date)}</div>
                  <div><span className="text-muted-foreground">Urgency: </span>{selected.urgency || '-'}</div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span>{selected.category_name || CATEGORY_LABELS[selected.category_id] || selected.category_id}</span>
                  </div>
                  <div><span className="text-muted-foreground">Scale: </span>{selected.scale || '-'}</div>
                  <div><span className="text-muted-foreground">Budget: </span>{selected.budget || '-'}</div>
                  <div><span className="text-muted-foreground">Submitted: </span>{formatDate(selected.submitted_at)}</div>
                </div>

                {selected.subtypes?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Subtypes</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.subtypes.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
                    </div>
                  </div>
                )}

                {selected.category_fields && Object.keys(selected.category_fields).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Additional Details</p>
                    <div className="grid grid-cols-2 gap-2 text-sm bg-muted/50 rounded-lg p-3">
                      {Object.entries(selected.category_fields).map(([label, value]) => (
                        <div key={label}>
                          <span className="text-muted-foreground">{label}: </span>{value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Justification</p>
                  <p className="text-sm bg-muted/50 rounded-lg p-3">{selected.justification || '-'}</p>
                </div>

                {selected.support_needs && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Support Needs</p>
                    <p className="text-sm">{selected.support_needs}</p>
                  </div>
                )}

                {selected.reporting_needs && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Reporting Needs</p>
                    <p className="text-sm">{selected.reporting_needs}</p>
                  </div>
                )}

                {selected.dependencies?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Dependencies</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.dependencies.map((d) => <Badge key={d} variant="outline">{d}</Badge>)}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">Triage / Review</p>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Status</label>
                    <Select value={reviewStatus} onValueChange={setReviewStatus}>
                      <SelectTrigger data-testid="review-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Review Comments</label>
                    <Textarea
                      data-testid="review-comments-textarea"
                      placeholder="Add notes on how this request was handled..."
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      rows={3}
                    />
                  </div>
                  {selected.reviewed_by && (
                    <p className="text-xs text-muted-foreground">
                      Last reviewed by {selected.reviewed_by} on {formatDate(selected.reviewed_at)}
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)} data-testid="cancel-review-btn">
                  Cancel
                </Button>
                <Button onClick={handleSaveReview} disabled={saving} data-testid="save-review-btn">
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" />
              Delete Service Request{deleteTarget?.ids.length > 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete {deleteTarget?.ids.length} service request{deleteTarget?.ids.length > 1 ? 's' : ''}. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceRequestsPage;
